import { useMutation, useQueryClient } from "@tanstack/react-query"
import type {
	ProjectDTO,
	ProjectMembersResponse,
	UserSummaryDTO,
} from "@zplanem/responses"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError, apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { useJournalCacheInvalidator } from "../hooks/use-journal-cache-invalidator"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

function displayName(u: UserSummaryDTO): string {
	const full = [u.firstName, u.lastName].filter(Boolean).join(" ")
	return full.length > 0 ? full : u.email
}

export function AddProjectMemberModal({
	propertyId,
	project,
	onClose,
}: {
	propertyId: string
	project: ProjectDTO
	onClose: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const queryClient = useQueryClient()
	const invalidateJournal = useJournalCacheInvalidator(propertyId)
	const memberships = usePropertyMemberships(propertyId)
	const [formError, setFormError] = useState<string | null>(null)

	const addMember = useMutation({
		mutationFn: (userId: string) =>
			apiFetch<ProjectMembersResponse>(
				`/api/common/properties/${propertyId}/projects/${project.id}/members`,
				{ method: "POST", body: JSON.stringify({ userId }) },
			),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.projects(propertyId),
			})
			invalidateJournal()
			onClose()
		},
	})

	// Match create-project-modal.tsx's close idiom: Escape dismisses the dialog.
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	const currentMemberIds = new Set((project.members ?? []).map((m) => m.id))
	const candidates: UserSummaryDTO[] = []
	if (memberships.data) {
		if (!currentMemberIds.has(memberships.data.owner.id)) {
			candidates.push(memberships.data.owner)
		}
		for (const m of memberships.data.memberships) {
			if (m.role === "contractor") continue
			if (currentMemberIds.has(m.user.id)) continue
			candidates.push(m.user)
		}
	}

	async function onPick(userId: string) {
		setFormError(null)
		try {
			await addMember.mutateAsync(userId)
		} catch (err) {
			setFormError(
				err instanceof ApiError ? err.message : t.projects.form.errors.unknown,
			)
		}
	}

	return (
		// Overlay + dialog wrapper mirrors create-project-modal.tsx: role/aria-modal
		// on the overlay, a full-bleed backdrop button for click-to-dismiss, an
		// Escape handler (above), and a × close button in the header.
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="add-project-member-title"
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={t.projects.form.cancel}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-md rounded-lg border border-border bg-popover text-popover-foreground p-6 shadow-2xl">
				<div className="flex items-start justify-between">
					<h2
						id="add-project-member-title"
						className="text-xl font-bold text-foreground"
					>
						{t.projects.detail.addMemberTitle}
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground px-2 py-1"
						aria-label={t.projects.form.cancel}
					>
						×
					</button>
				</div>
				<div className="mt-4 space-y-1 max-h-72 overflow-y-auto">
					{memberships.isLoading ? (
						<p className="text-sm text-muted-foreground">{t.common.loading}</p>
					) : candidates.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							{t.projects.detail.addMemberEmpty}
						</p>
					) : (
						candidates.map((u) => (
							<button
								key={u.id}
								type="button"
								onClick={() => onPick(u.id)}
								disabled={addMember.isPending}
								className="w-full text-left px-3 py-2.5 rounded-xl text-sm text-foreground/80 hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
							>
								<span className="font-medium">{displayName(u)}</span>
								<span className="block text-xs text-muted-foreground/80">
									{u.email}
								</span>
							</button>
						))
					)}
				</div>
				{formError ? (
					<div
						role="alert"
						className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
					>
						{formError}
					</div>
				) : null}
				<div className="mt-5 flex justify-end">
					<Button variant="ghost" onClick={onClose}>
						{t.projects.form.cancel}
					</Button>
				</div>
			</div>
		</div>
	)
}
